"use client";

import React from "react";
import { motion } from "framer-motion";
import { Quote, Sparkles, CircleCheck as CheckCircle2, Lightbulb, ArrowRight, BookOpen } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const coreComponents = [{
  title: "Complete Transparency & Full Data Ownership",
  description: "You see every crawl error, ranking movement, and backlink change yourself in real time, without filtered agency summaries or obscured metrics."
}, {
  title: "Faster Implementation",
  description: "You can resolve flagged issues and publish on-page optimisations the same day instead of waiting on protracted agency review cycles."
}, {
  title: "Full Control Over Priorities",
  description: "You decide whether to focus your resources on local SEO, content optimisation, or technical fixes based on what drives immediate commercial value."
}];
export default function Definition() {
  return <section id="diyseodefinition" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground font-sans overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center space-y-4 mb-10">
          <div className="flex items-center justify-center gap-2">
            <Badge variant="secondary" className="px-3 py-1 bg-secondary text-secondary-foreground border-secondary/50 font-medium">
              <Sparkles className="w-3.5 h-3.5 text-accent mr-1" />
              SEO Glossary &amp; Definition
            </Badge>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-primary tracking-tight">
            What is DIY SEO Software?
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
            A clear, direct guide to understanding self-serve search engine optimization software for modern businesses.
          </p>
        </motion.div>

        {/* Separator */}
        <div className="w-full max-w-xs mx-auto mb-10">
          <Separator className="bg-secondary h-0.5" />
        </div>

        {/* Direct Answer Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="mb-12">
          <Card className="bg-card text-card-foreground border border-border shadow-md rounded-2xl overflow-hidden">
            <CardContent className="p-6 md:p-10 space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-secondary rounded-xl text-primary shrink-0 hidden sm:block">
                  <BookOpen className="w-6 h-6" />
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-accent bg-secondary/80 px-2.5 py-0.5 rounded-md">
                      Direct Answer
                    </span>
                    <span className="text-xs text-muted-foreground">Updated for modern &amp; AI Search</span>
                  </div>
                  <p className="text-lg md:text-xl font-normal leading-relaxed text-foreground font-sans">
                    <strong className="font-semibold text-primary">DIY SEO software</strong> lets business owners, in-house marketers, and founders perform keyword research, technical SEO audits, content optimisation, rank tracking, and competitor analysis independently without hiring an agency. While traditional agency retainers typically cost £500–£1,000+ per month, a self-serve DIY SEO tool stack generally ranges between $50 and $300/month, giving you direct control over your search data and actionable guidance to grow organic visibility on your own schedule.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Pull-Quote */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="mb-12">
          <div className="relative bg-muted/50 border-l-4 border-accent rounded-r-2xl p-6 md:p-8 space-y-4 shadow-xs">
            <Quote className="w-8 h-8 text-accent/40 absolute top-4 right-4" />
            <p className="text-base md:text-lg italic text-foreground leading-relaxed font-sans pr-6">
              {"\"DIY SEO software puts raw search intelligence directly into your hands. Investing $50 to $300 a month in the right software stack lets you diagnose issues, spot keyword opportunities, and act immediately—giving you total ownership of your search presence without the overhead of a rigid agency retainer.\""}
            </p>
            <div className="flex items-center gap-3 pt-2">
              <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm">
                YE
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground font-serif">Yassine Essaadi</p>
                <p className="text-xs text-muted-foreground">Freelance SEO Consultant, YE Consultancy</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Separator */}
        <div className="w-full max-w-md mx-auto mb-12">
          <Separator className="bg-secondary/70 h-0.5" />
        </div>

        {/* Core Principles */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="space-y-6 mb-12">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-5 h-5 text-primary" />
            <h3 className="text-xl md:text-2xl font-bold font-serif text-primary">
              Core Principles of DIY Search Engine Software
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {coreComponents.map((item, i) => <Card data-index={i} key={i} className="bg-card text-card-foreground border border-border rounded-xl shadow-xs hover:shadow-md transition-[color,background-color,border-color,box-shadow]">
                <CardContent className="p-6 space-y-3">
                  <div className="flex items-center gap-2 text-primary font-semibold text-base font-serif">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                    <h4>{item.title}</h4>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                    {item.description}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </motion.div>

        {/* Closing Consultation Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="bg-card border border-border rounded-2xl p-6 md:p-8 text-center space-y-4 shadow-sm">
          <h3 className="text-xl md:text-2xl font-bold font-serif text-primary">
            Need Expert Strategy Beyond Software?
          </h3>
          <p className="text-muted-foreground text-sm md:text-base max-w-xl mx-auto font-sans">
            Deciding between self-serve DIY SEO software and hiring an independent consultant comes down to your internal bandwidth, technical complexity, and growth goals. Let&apos;s evaluate where software is sufficient and where bespoke strategic support delivers higher commercial returns.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-accent/10">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}